// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The budgets of 11 §8 (M9-30).
//
// *"Bounded so that a published machine cannot degrade anyone's device. Each limit is enforced at
// **save**, again at **publish**, and again at **load**."*
//
// **These are not format rules, and that is why they live here rather than in `validate.ts`.** 04
// §16 bounds an art surface and a display scene because those are load-time costs the *renderer*
// cares about, and says nothing about 120 devices or a 512 KB document — deliberately, because
// those are limits about somebody else's phone rather than about whether a document is a machine.
// A machine over budget is well-formed and refused anyway.
//
// **One table, three call sites.** The milestone's own wording is "enforced at save, again at
// publish, and again at load", and three enforcements of one rule is three chances to disagree
// about it. So this is the table, and the Builder's save, the server's publish and the client's
// load all ask it. A second copy of the number 120 is the bug this file exists to prevent.
//
// **The 512 KB limit is not here, and that is the core's purity rule doing its job.** Measuring it
// needs `TextEncoder`, which is a platform global the core may not reach for — and the guard also
// refuses the *word* `document`, because a core that mentions one is a core somebody will one day
// read from. It belongs on the server anyway: the limit is on a *request body*, and the server is
// the only one of the three call sites that has bytes rather than an object.
//
// Pure, and in the core because all three callers are on different sides of the network from each
// other.

/** 11 §8's table, less the byte limit — see the header for why that one is the server's. */
export const BUDGETS = {
  colliders: 400,
  devices: 120,
  lamps: 160,
  rules: 400,
  modes: 24,
  /** Simultaneous balls. Six is two multiballs' worth and more than any real machine serves. */
  balls: 6,
  /** Per surface. 04 §13's own limit of 64 is stricter and bites first; both are enforced. */
  artOpsPerSurface: 250,
  displayScenes: 64,
  audioCues: 120,
} as const

/**
 * The code a budget overrun is reported under.
 *
 * Named here rather than written at each call site: three places map an overrun into an issue, and
 * three spellings of one code is a panel that sorts two of them into a class and loses the third.
 */
export const BUDGET_CODE = 'over-budget'

export interface BudgetOverrun {
  /** The member, as 04 spells it. */
  readonly path: string
  /** What the machine declares. */
  readonly count: number
  readonly limit: number
  readonly message: string
}

/**
 * Everything this machine declares more of than it may.
 *
 * Takes an unknown rather than a `MachineDefinition`, because two of the three callers have bytes
 * that have not been validated yet — the whole point of a budget at save is that it is checked
 * before anybody has agreed the document is a machine.
 */
export function overBudget(definition: unknown): readonly BudgetOverrun[] {
  if (!isRecord(definition)) return []
  const out: BudgetOverrun[] = []
  const rules = isRecord(definition['rules']) ? definition['rules'] : {}

  check('devices', countOf(definition['devices']), BUDGETS.devices)
  check('lamps', countOf(definition['lamps']), BUDGETS.lamps)
  check('rules.rules', countOf(rules['rules']), BUDGETS.rules)
  check('rules.modes', countOf(rules['modes']), BUDGETS.modes)
  check('display.scenes', countOf(nested(definition, 'display', 'scenes')), BUDGETS.displayScenes)
  check('audio.cues', keyCount(definition, 'audio', 'cues'), BUDGETS.audioCues)
  check('physics.balls', numberAt(definition, 'physics', 'balls'), BUDGETS.balls)

  // **Colliders are counted the way the loader builds them**, not from a member: a device declares
  // one or several and a ramp declares a run. Two per layer and one per device is the shape of it,
  // and it is deliberately an over-count rather than an under-count — a budget that lets a machine
  // through and then finds out on somebody's phone is worse than one that is slightly mean.
  check(
    'colliders',
    countOf(definition['devices']) + countOf(definition['layers']) * 2,
    BUDGETS.colliders,
  )

  for (const [path, count] of artSurfaces(definition)) {
    check(path, count, BUDGETS.artOpsPerSurface)
  }
  return out

  function check(path: string, count: number, limit: number): void {
    if (count <= limit) return
    out.push({
      path,
      count,
      limit,
      message: `${count} exceeds the ${limit} a machine may declare`,
    })
  }
}

function artSurfaces(definition: Record<string, unknown>): readonly [string, number][] {
  const art = definition['art']
  if (!isRecord(art)) return []
  const out: [string, number][] = []
  for (const key of ['playfield', 'backglass', 'plastics']) {
    const surface = art[key]
    if (isRecord(surface)) out.push([`art.${key}.program`, countOf(surface['program'])])
  }
  const cabinet = art['cabinet']
  if (isRecord(cabinet)) {
    for (const face of Object.keys(cabinet).sort()) {
      const surface = cabinet[face]
      if (isRecord(surface)) out.push([`art.cabinet.${face}.program`, countOf(surface['program'])])
    }
  }
  return out
}

function nested(definition: Record<string, unknown>, outer: string, inner: string): unknown {
  const block = definition[outer]
  return isRecord(block) ? block[inner] : undefined
}

function numberAt(definition: Record<string, unknown>, outer: string, inner: string): number {
  const value = nested(definition, outer, inner)
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function keyCount(definition: Record<string, unknown>, outer: string, inner: string): number {
  const map = nested(definition, outer, inner)
  return isRecord(map) ? Object.keys(map).length : 0
}

function countOf(value: unknown): number {
  return Array.isArray(value) ? value.length : 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
